/**
 * App-mode detection: kiosk vs digital.
 *
 * The renderer is a single bundle deployed to multiple domains, plus
 * loaded by the Electron shell as a remote URL. Whether we behave as
 * a full operator-facing kiosk renderer or as a consumer-facing
 * digital renderer is decided here, at runtime, in this order:
 *
 *   1. Desktop shell — Electron and Tauri are ALWAYS kiosk. This is the
 *      hard guarantee that live booths never regress; it short-circuits
 *      before any URL / storage inspection.
 *   2. Explicit `?mode=kiosk|digital` override (QA / demos), made sticky.
 *   3. Sticky per-tab resolution — once a browser tab resolves *digital*
 *      we keep it, so digital-only routes that aren't slugs (`/new`,
 *      `/g/:id`) and any navigation that drops the query param stay digital.
 *   4. Host — `dreambooth.app` (and subdomains) is digital.
 *   5. Path — a bare *slug* path (a single non-reserved, non-ObjectId
 *      segment, e.g. `/panoramic`) is a digital booth on ANY host. This
 *      is what lets `app.dreamboothstudio.com/panoramic` behave like the
 *      future `dreambooth.app/panoramic` without a query param.
 *
 * Kiosk is the default. Crucially, ObjectId paths (`/{24-hex}`) — the ONLY
 * shape kiosk ever uses for a project — and the operator routes stay kiosk,
 * so this never regresses the kiosk renderer. Kiosk resolution never writes
 * the sticky flag, so a kiosk tab can never be "poisoned" into digital.
 *
 * There is intentionally NO client-side feature-flag gate. Digital access is
 * pilot-gated on the SERVER, keyed on the *project owner's* `digital_mode`
 * flag (`/api/projects/by-slug`), so an anonymous / incognito visitor can
 * open an allowlisted owner's booth — a visitor has no email, so a
 * visitor-keyed flag could never resolve for them.
 */

import { hasElectronPreload } from './bridge'
import { isInTauriShell } from './detector'

export type AppMode = 'kiosk' | 'digital'

/** Hosts that are digital by default. Case-insensitive suffix match. */
const DIGITAL_HOSTS = ['dreambooth.app'] as const

/**
 * First path segments that are NOT project slugs — operator routes plus the
 * digital-only routes that aren't slugs. A first segment in this set never
 * triggers slug-based digital detection.
 */
const RESERVED_SEGMENTS = new Set([
  '',
  'login',
  'register',
  'print',
  'upload',
  'strip',
  'new',
  'birthday',
  'g',
  'gallery'
])

/** Mongo ObjectId — the shape kiosk uses for `/:projectId`. */
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

/** sessionStorage key for the sticky per-tab resolved mode. */
const STICKY_KEY = 'db.appMode'

function matchesDigitalHost(host: string): boolean {
  const h = host.toLowerCase()
  return DIGITAL_HOSTS.some((d) => h === d || h.endsWith(`.${d}`))
}

function firstPathSegment(): string {
  try {
    return window.location.pathname.split('/').filter(Boolean)[0] ?? ''
  } catch {
    return ''
  }
}

/**
 * True when the URL path is a bare project *slug* (a single segment that is
 * neither an operator/digital route nor a Mongo ObjectId). Kiosk always
 * addresses projects by ObjectId, so a slug path unambiguously means digital.
 */
function isSlugPath(): boolean {
  const seg = firstPathSegment()
  if (!seg) return false
  if (RESERVED_SEGMENTS.has(seg.toLowerCase())) return false
  if (OBJECT_ID_RE.test(seg)) return false
  return true
}

function readSticky(): AppMode | null {
  try {
    const v = window.sessionStorage.getItem(STICKY_KEY)
    return v === 'digital' || v === 'kiosk' ? v : null
  } catch {
    return null
  }
}

function writeSticky(mode: AppMode): void {
  try {
    window.sessionStorage.setItem(STICKY_KEY, mode)
  } catch {
    // Private mode / storage disabled — non-fatal, we just re-resolve.
  }
}

/**
 * Returns the current app mode. Pure function of runtime + URL.
 *
 * Safe to call from module scope (returns `'kiosk'` when `window` is
 * undefined, preserving existing SSR-safe behavior elsewhere).
 */
export function getAppMode(): AppMode {
  // 1. Desktop shells are always kiosk — hard guarantee, before anything else.
  if (hasElectronPreload() || isInTauriShell()) return 'kiosk'
  if (typeof window === 'undefined') return 'kiosk'

  // 2. Explicit override wins and is pinned (survives navigation).
  try {
    const q = new URL(window.location.href).searchParams.get('mode')
    if (q === 'digital' || q === 'kiosk') {
      writeSticky(q)
      return q
    }
  } catch {
    // Malformed location.href — fall through.
  }

  // 3. Sticky (only ever set to 'digital' by prior digital resolution, or by
  //    an explicit override) — keeps digital-only routes (/new, /g) digital.
  const sticky = readSticky()
  if (sticky) return sticky

  // 4. Digital domain.
  if (matchesDigitalHost(window.location.hostname)) {
    writeSticky('digital')
    return 'digital'
  }

  // 5. Bare slug path on any other host → digital. Pin so /new, /g stay digital.
  if (isSlugPath()) {
    writeSticky('digital')
    return 'digital'
  }

  // Default: kiosk. Deliberately NOT pinned, so a kiosk tab never sticks.
  return 'kiosk'
}

export const isDigitalMode = (): boolean => getAppMode() === 'digital'
export const isKioskMode = (): boolean => getAppMode() === 'kiosk'
